import type { CSSProperties, ReactNode } from "react";
import { STATUS_PALETTE } from "@/lib/tokens";

export type CountPillTone = "open" | "overdue" | "done" | "neutral";

interface CountPillProps {
  tone: CountPillTone;
  children: ReactNode;
}

const base: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 8,
  padding: "3px 9px",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
};

// "done" reuses the existing STATUS_PALETTE green + `.flow-status-tint` CSS
// class (same dark-mode-safe translucent-chip mechanism as StatusBadge).
const doneTint = STATUS_PALETTE.green;

/** Small numeric/status pill: "N offen" / "alles erledigt" / Frist states.
 * `open`/`overdue` derive their translucent tint straight from the semantic
 * `--warn`/`--danger` tokens (which already flip per theme), never a
 * hardcoded hex — see flow_dark_mode_token_rule. */
export function CountPill({ tone, children }: CountPillProps) {
  if (tone === "done") {
    return (
      <span
        data-testid="count-pill-done"
        className="flow-status-tint"
        style={
          {
            ...base,
            "--pill-bg": doneTint.bg,
            "--pill-fg": doneTint.fg,
            "--pill-dot": doneTint.dot,
          } as CSSProperties
        }
      >
        {children}
      </span>
    );
  }

  const variant: CSSProperties =
    tone === "open"
      ? {
          background: "color-mix(in srgb, var(--warn) 18%, transparent)",
          color: "var(--warn)",
        }
      : tone === "overdue"
        ? {
            background: "color-mix(in srgb, var(--danger) 18%, transparent)",
            color: "var(--danger)",
          }
        : { background: "var(--surface-soft)", color: "var(--ink-2)" };

  return (
    <span data-testid={`count-pill-${tone}`} style={{ ...base, ...variant }}>
      {children}
    </span>
  );
}
